import type { NextRequest, NextResponse } from 'next/server'

import { configPublic } from '@src/config.public'
import { getLocaleFromPath, getLocaleFromURL } from '@src/i18n/locale.util'
import { COOKIES, QUERY_PARAMS } from '@src/utils/constants.util'
import { isomorphicCookies } from '@src/utils/cookies.util'
import { getShopifyAccountLogoutUrlFromRedirect } from '@src/utils/externalShopifyLink'
import { getCurrentURL, getShopifyAccountLogoutUrl } from '@src/utils/url.util'

const LOGOUT_REASONS: Auth.LogoutReason[] = ['inactivity', 'refresh_failed']

export const AuthUtils = {
  getQueryParamNames,
  getLoginUrl,
  getIsCheckingSSO,
  clearCheckingSSO,
  getIsComingFromCallback,
  clearComingFromCallback,
  getIsSSOCheckedRecently,
  parseLogoutReason,
  getLogoutRedirectUrl,
}

// Shopify holds a separate session, so OneSite P2 also logs out of the Shopify Account.
function getLogoutRedirectUrl(redirectUrl: URL): URL {
  if (!configPublic.featureFlags.onesiteP2Enabled) return redirectUrl

  const accountLogoutUrl = getShopifyAccountLogoutUrlFromRedirect(redirectUrl)
  if (accountLogoutUrl) return accountLogoutUrl

  const locale = getLocaleFromPath(redirectUrl.pathname)
  const logoutUrl = getShopifyAccountLogoutUrl(locale.code)
  return logoutUrl ? new URL(logoutUrl) : redirectUrl
}

function getQueryParamNames() {
  return {
    redirectUrl: QUERY_PARAMS.redirectUrl,
    locale: QUERY_PARAMS.authLocale,
    logoutReason: QUERY_PARAMS.logoutReason,
  }
}

async function getLoginUrl(args?: Auth.LoginArgs) {
  const currentUrl = await getCurrentURL()
  const loginUrl = new URL('/login', currentUrl.origin)
  const queryParams = getQueryParamNames()
  loginUrl.searchParams.append(queryParams.redirectUrl, args?.redirectUrl ?? currentUrl.toString())
  loginUrl.searchParams.append(
    queryParams.locale,
    args?.locale ?? getLocaleFromURL(currentUrl).code
  )
  return loginUrl.toString()
}

function parseLogoutReason(value?: string | null): Auth.LogoutReason | null {
  if (!value) return null
  return LOGOUT_REASONS.includes(value as Auth.LogoutReason) ? (value as Auth.LogoutReason) : null
}

async function getIsCheckingSSO(request?: NextRequest): Promise<boolean> {
  const cookies = isomorphicCookies({ request })
  return await cookies.has(COOKIES.auth0CheckingSSO)
}

async function clearCheckingSSO(request?: NextRequest, response?: NextResponse) {
  const cookies = isomorphicCookies({ request, response })
  await cookies.delete(COOKIES.auth0CheckingSSO)
}

async function getIsComingFromCallback(request?: NextRequest): Promise<boolean> {
  const cookies = isomorphicCookies({ request })
  return await cookies.has(COOKIES.auth0ComingFromCallback)
}

async function clearComingFromCallback(request?: NextRequest, response?: NextResponse) {
  const cookies = isomorphicCookies({ request, response })
  await cookies.delete(COOKIES.auth0ComingFromCallback)
}

async function getIsSSOCheckedRecently(request?: NextRequest): Promise<boolean> {
  const cookies = isomorphicCookies({ request })
  return await cookies.has(COOKIES.auth0SSOCheckedRecently)
}
